import React, { useState } from 'react';

import {
  ActionIcon,
  Avatar,
  Button,
  Flex,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  PaperProps,
  Select,
  Space,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconDotsVertical, IconListDetails, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserContext } from 'contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { TeamService } from 'services/team-service';
import { MY_TEAMS_QUERY_KEY } from 'shared/constants/query-keys';
import { Team, UpdateTeamForm } from 'shared/interfaces/Team';
import { User, UserAccess } from 'shared/interfaces/User';
import {
  TEAM_MAX_NUM_MEMBERS,
  TEAM_NAME_MAX_LENGTH,
  maxLengthValidator,
  maxStringArrayLengthValidator,
  nonEmptyArrayValidator,
  requiredInputValidator
} from 'shared/utils/form-validators';

const CARD_WIDTH = 220;
const CARD_HEIGHT = 80;

interface TeamCardProps extends PaperProps {
  team: Team;
  allUsers: User[];
}

interface UpdateTeamVariables {
  teamId: string;
  updateTeamForm: UpdateTeamForm;
}

export function TeamCard({ team, allUsers, ...paperProps }: TeamCardProps) {
  const { t } = useTranslation();
  const [loggedInUser] = useUserContext();
  const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState<boolean>(false);
  const [isUpdatingTeam, setIsUpdatingTeam] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Helpers
  const getUserIdByEmail = (email: string) => {
    const user = allUsers.find((u) => u.email === email);
    return user ? user._id : undefined;
  };

  const getUserEmailById = (userId: string) => {
    const user = allUsers.find((u) => u._id === userId);
    return user ? user.email : undefined;
  };

  const getTeamOwnerEmail = (): string => {
    const owner = allUsers.find((user) => user._id === team.owner);
    return owner ? owner.email : '';
  };

  const convertTeamMembersIdsToEmails = (teamMembersIds: string[]): string[] => {
    return teamMembersIds.reduce((userEmails: string[], userId: string) => {
      const userEmail = getUserEmailById(userId);
      if (userEmail) userEmails.push(userEmail);
      return userEmails;
    }, []);
  };

  const getInitialsOrFirstTwoLetters = (name: string) => {
    const words = name.split(' ');
    if (words.length > 1) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const nonOwnerOrSuperAdminMembers = team.members.filter((userId) => {
    const user = allUsers.find((u) => u._id === userId);
    return user && user.role !== UserAccess.SUPER_ADMIN && user._id !== team.owner;
  });

  const loggedInUserCanEditTeam = loggedInUser._id === team.owner || loggedInUser.role === UserAccess.SUPER_ADMIN;
  const loggedInUserIsSuperAdmin = loggedInUser.role === UserAccess.SUPER_ADMIN;

  // Mutations
  const deleteTeamMutation = useMutation({
    mutationFn: TeamService.deleteTeam,
    onSuccess: (deletedTeam: Team) => {
      queryClient.setQueryData([MY_TEAMS_QUERY_KEY], (oldTeams: Team[] = []) => {
        return oldTeams.filter((oldTeam) => oldTeam._id !== deletedTeam._id);
      });
      NotificationService.showResult(true, t('FEED_BACK.TEAMS.SUCCESS.DELETE'));
    },
    onError: () => NotificationService.showResult(false, t('FEED_BACK.TEAMS.FAILURE.DELETE.UNKNOWN'))
  });

  const updateTeamMutation = useMutation({
    mutationFn: (updateTeamVariables: UpdateTeamVariables) => {
      // Get the ids of the selected users based on their emails
      const selectedUsersIds = updateTeamVariables.updateTeamForm.membersEmails.reduce(
        (userIds: string[], email: string) => {
          const userId = getUserIdByEmail(email);
          if (userId) userIds.push(userId);
          return userIds;
        },
        []
      );

      return TeamService.updateTeam(updateTeamVariables.teamId, {
        name: updateTeamVariables.updateTeamForm.name,
        owner: updateTeamVariables.updateTeamForm.ownerEmail
          ? getUserIdByEmail(updateTeamVariables.updateTeamForm.ownerEmail)
          : undefined,
        members: selectedUsersIds
      });
    },
    onMutate: () => setIsUpdatingTeam(true),
    onSuccess: (updatedTeam: Team) => {
      setIsUpdatingTeam(false);
      queryClient.setQueryData([MY_TEAMS_QUERY_KEY], (oldTeams: Team[] = []) => {
        return oldTeams.map((oldTeam) => {
          if (oldTeam._id === updatedTeam._id) return updatedTeam;
          return oldTeam;
        });
      });
      closeEditTeamModal();
      NotificationService.showResult(true, t('FEED_BACK.TEAMS.SUCCESS.UPDATE'));
    },
    onError: () => {
      setIsUpdatingTeam(false);
      NotificationService.showResult(false, t('FEED_BACK.TEAMS.FAILURE.UPDATE.UNKNOWN'));
    }
  });

  // Forms
  const editTeamForm = useForm({
    initialValues: {
      name: team.name,
      ownerEmail: getTeamOwnerEmail(),
      membersEmails: team.members ? convertTeamMembersIdsToEmails(team.members) : []
    },
    validate: {
      name: (value) => requiredInputValidator(value) || maxLengthValidator(value, TEAM_NAME_MAX_LENGTH),
      ownerEmail: (value) => requiredInputValidator(value),
      membersEmails: (value) =>
        nonEmptyArrayValidator(value) || maxStringArrayLengthValidator(value, TEAM_MAX_NUM_MEMBERS)
    }
  });

  // Open/Close modals
  const openEditTeamModal = () => setIsEditTeamModalOpen(true);
  const closeEditTeamModal = () => setIsEditTeamModalOpen(false);

  return (
    <>
      <Paper p={10} pr={0} withBorder w={CARD_WIDTH} h={CARD_HEIGHT} {...paperProps}>
        <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
          <Flex direction="row" align="center">
            <Avatar radius={20} size={40} src={null}>
              {getInitialsOrFirstTwoLetters(team.name)}
            </Avatar>
            <Space w={15} />
            <Flex direction="column">
              <Title order={6} truncate maw={CARD_WIDTH / 2}>
                {team.name}
              </Title>
              <Text color="dimmed">
                {t('ORGANISATION.TEAMS.MEMBERS_NUMBER', {
                  number: nonOwnerOrSuperAdminMembers.length > 100 ? '+100' : nonOwnerOrSuperAdminMembers.length
                })}
              </Text>
            </Flex>
          </Flex>
          <Menu position="right-start" withArrow arrowPosition="center" offset={0}>
            <Menu.Target>
              <ActionIcon onClick={(event) => event.stopPropagation()}>
                <IconDotsVertical />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
              <Menu.Item onClick={openEditTeamModal} icon={<IconListDetails />}>
                {t('APP.ACTIONS.EDIT')}
              </Menu.Item>
              <Menu.Item
                disabled={!loggedInUserCanEditTeam}
                icon={<IconTrash />}
                color="red"
                onClick={() => deleteTeamMutation.mutate(team._id)}>
                {t('APP.ACTIONS.DELETE')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Paper>
      <Modal title={t('ORGANISATION.TEAMS.EDIT_MODAL.TITLE')} onClose={closeEditTeamModal} opened={isEditTeamModalOpen}>
        <TextInput
          label={t('ORGANISATION.TEAMS.NAME_LABEL')}
          placeholder={t('ORGANISATION.TEAMS.NAME_PLACEHOLDER')}
          {...editTeamForm.getInputProps('name')}
        />

        <Space h="md" />

        <Select
          disabled={!loggedInUserIsSuperAdmin}
          data={allUsers
            .filter((user) => user.role === UserAccess.ADMIN || user.role === UserAccess.SUPER_ADMIN)
            .map((user) => user.email)}
          label={t('ORGANISATION.TEAMS.EDIT_MODAL.TEAM_OWNER_LABEL')}
          placeholder={t('ORGANISATION.TEAMS.EDIT_MODAL.TEAM_OWNER_PLACEHOLDER')}
          searchable
          nothingFound={t('ORGANISATION.TEAMS.EDIT_MODAL.NO_USERS_FOUND')}
          limit={20}
          {...editTeamForm.getInputProps('ownerEmail')}
        />

        <Space h="md" />

        <MultiSelect
          data={
            // Can't select super admin or owner to add to the team
            allUsers
              .filter((user) => user.role !== UserAccess.SUPER_ADMIN && user._id !== team.owner)
              .map((user) => user.email)
          }
          label={t('ORGANISATION.TEAMS.EDIT_MODAL.MEMBERS_LABEL')}
          placeholder={t('ORGANISATION.TEAMS.EDIT_MODAL.MEMBERS_PLACEHOLDER')}
          searchable
          nothingFound={t('ORGANISATION.TEAMS.EDIT_MODAL.NO_USERS_FOUND')}
          maxSelectedValues={TEAM_MAX_NUM_MEMBERS}
          limit={20}
          {...editTeamForm.getInputProps('membersEmails')}
        />

        <Space h="md" />

        <Button
          my="xs"
          style={{ float: 'right' }}
          loading={isUpdatingTeam}
          onClick={() => {
            editTeamForm.validate();
            if (editTeamForm.isValid()) {
              setIsUpdatingTeam(true);
              updateTeamMutation.mutate({
                teamId: team._id,
                updateTeamForm: {
                  name: editTeamForm.values.name,
                  // If the owner email has changed, we need to update the owner
                  ownerEmail:
                    editTeamForm.values.ownerEmail !== '' && editTeamForm.values.ownerEmail !== getTeamOwnerEmail()
                      ? editTeamForm.values.ownerEmail
                      : undefined,
                  membersEmails: editTeamForm.values.membersEmails
                }
              });
            }
          }}>
          {t('ORGANISATION.TEAMS.UPDATE_BUTTON_LABEL')}
        </Button>
      </Modal>
    </>
  );
}
